import { useEffect } from "react";
import { useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import { FACILITY_DETAILS_CONTACT_QUERY, FacilityDetailsContactQueryResponse } from "@/features/facility/queries";
import { CardSkeleton } from "@/components/Skeleton";
import { ContactDetails } from "@/features/facility/components/ContactDetails";
import { useRecentlyVisited } from "@/hooks/useRecentlyVisited";
import ErrorBoundary from "@/components/ErrorBoundary";

export default function FacilityDetailsContactPage() {
  const { facilityId } = useParams();
  const { addRecentlyVisitedItem } = useRecentlyVisited();

  const { data, error, loading } = useQuery<FacilityDetailsContactQueryResponse>(FACILITY_DETAILS_CONTACT_QUERY, {
    variables: { facilityId },
    errorPolicy: "all"
  });

  useEffect(() => {
    if (data?.facility) {
      addRecentlyVisitedItem({
        url: window.location.pathname,
        text: data.facility.name,
        subtext: data.facility.section?.name || "",
        image: "store",
        type: "facilities"
      });
    }
  }, [data, addRecentlyVisitedItem]);

  if (loading && !data?.facility) {
    return (
      <div className="flex mt-4 gap-4">
        <CardSkeleton maxWidth={60} />
        <CardSkeleton maxWidth={60} />
      </div>
    );
  }

  if (!data?.facility) {
    return (
      <ErrorBoundary
        variant="inline"
        title="Failed to Load Contact Details"
        message="We couldn't load contact details for this facility. Please contact tech support."
        className="mt-12"
      />
    );
  }

  return (
    <div>
      {error && (
        <ErrorBoundary
          variant="inline"
          title="Some facility contact details couldn't be loaded"
          message="Parts of this page may be missing. Please contact tech support if information looks incomplete."
          className="mt-12"
        />
      )}
      <ContactDetails data={data} facilityId={facilityId!} />
    </div>
  );
}
